/*
 * Purpose: AI flow summary when no map block is selected — live sync with map graph
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Loader2, Target, ListChecks, RefreshCw, Radar, WandSparkles, Wand2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useWorkflowStore } from '@/stores/workflowStore';
import {
  buildRuleBasedFlowSummary,
  generateFlowSummaryAsync,
  hasFlowSummaryAi,
  invalidateFlowSummaryCache,
  workflowGraphFingerprint,
  type FlowSummary,
} from '@/services/autoblocks/flowSummary';
import { isMinimalMissionFlow, planAndApplyGoalToMap } from '@/lib/applyPlanToMap';
import { getMissionGoalIntent } from '@/lib/mapNodeSummary';
import { ContextualError } from '@/components/errors/ContextualError';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { cn } from '@/lib/utils';
import { analyzeInlineValidation, autoFixWorkflowInline, autoFixWorkflowInlineSafe } from '@/services/inlineValidation';
import { InlineValidationInsights } from '@/components/mission/InlineValidationInsights';
import { useExecutionTraceStore } from '@/stores/executionTraceStore';
import { toast } from 'sonner';
import { evaluateFlowTracingTruth } from '@/services/flowTracingTruth';
import { simulateFlowTrace } from '@/services/flowTraceSimulation';
import { useFlowTracingHistoryStore } from '@/stores/flowTracingHistoryStore';
import { useActivityLog } from '@/components/ActivityLog';

const AI_SYNC_DEBOUNCE_MS = 900;

interface FlowSummaryPanelProps {
  className?: string;
}

export function FlowSummaryPanel({ className }: FlowSummaryPanelProps) {
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);
  const updateMissionMeta = useWorkflowStore((s) => s.updateMissionMeta);
  const selectNode = useWorkflowStore((s) => s.selectNode);
  const applyAssembledWorkflowIfCurrent = useWorkflowStore((s) => s.applyAssembledWorkflowIfCurrent);
  const saveWorkflow = useWorkflowStore((s) => s.saveWorkflow);
  const { addActivity } = useActivityLog();
  const [summary, setSummary] = useState<FlowSummary | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [autoFixing, setAutoFixing] = useState(false);
  const [wiring, setWiring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const llmReady = hasFlowSummaryAi();
  const aiRequestRef = useRef(0);

  const graphFingerprint = useMemo(
    () => (currentWorkflow ? workflowGraphFingerprint(currentWorkflow) : null),
    [currentWorkflow]
  );
  const inlineValidation = useMemo(
    () => (currentWorkflow ? analyzeInlineValidation(currentWorkflow) : null),
    [currentWorkflow]
  );
  const trace = useExecutionTraceStore((s) =>
    currentWorkflow?.id ? s.traces[currentWorkflow.id] : undefined
  );
  const simulation = useMemo(
    () => (currentWorkflow ? simulateFlowTrace(currentWorkflow) : null),
    [currentWorkflow]
  );
  const liveStatus = trace?.running
    ? 'Live tracing active'
    : trace?.failedNodeId
      ? 'Last run failed at highlighted block'
      : simulation && !simulation.predictedComplete
        ? 'Pre-run simulation predicts flow is incomplete'
        : null;
  const flowTracingMode = currentWorkflow?.missionMeta?.flowTracingMode === true;
  const truthFactor = useMemo(
    () =>
      currentWorkflow ? evaluateFlowTracingTruth(currentWorkflow, trace, inlineValidation, simulation) : null,
    [currentWorkflow, trace, inlineValidation, simulation]
  );
  const pushSnapshot = useFlowTracingHistoryStore((s) => s.pushSnapshot);
  const lastGoodSnapshot = useFlowTracingHistoryStore((s) =>
    currentWorkflow?.id ? s.lastGoodSnapshot(currentWorkflow.id) : null
  );
  const compare = useFlowTracingHistoryStore((s) =>
    currentWorkflow?.id ? s.compareToLastGood(currentWorkflow.id) : null
  );

  useEffect(() => {
    if (!currentWorkflow || !truthFactor) return;
    const source = trace?.running ? 'run' : simulation && !simulation.predictedComplete ? 'simulation' : 'edit';
    pushSnapshot(currentWorkflow, truthFactor, source);
  }, [currentWorkflow, truthFactor, trace?.running, simulation, pushSnapshot]);

  useEffect(() => {
    if (!currentWorkflow || !graphFingerprint) {
      setSummary(null);
      return;
    }

    setError(null);
    setSummary(buildRuleBasedFlowSummary(currentWorkflow));
  }, [currentWorkflow, graphFingerprint]);

  useEffect(() => {
    if (!currentWorkflow || !graphFingerprint || !llmReady) {
      setAiLoading(false);
      return;
    }

    const requestId = ++aiRequestRef.current;
    setAiLoading(true);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const generated = await generateFlowSummaryAsync(currentWorkflow);
          if (aiRequestRef.current !== requestId) return;
          setSummary(generated);
          setError(null);
        } catch (err) {
          if (aiRequestRef.current !== requestId) return;
          setError(err instanceof Error ? err.message : 'Could not summarize this flow');
          setSummary(buildRuleBasedFlowSummary(currentWorkflow));
        } finally {
          if (aiRequestRef.current === requestId) setAiLoading(false);
        }
      })();
    }, AI_SYNC_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [currentWorkflow, graphFingerprint, llmReady]);

  const loadSummary = useCallback(
    async (force = false) => {
      if (!currentWorkflow) return;
      if (force) invalidateFlowSummaryCache(currentWorkflow.id);

      setError(null);
      setSummary(buildRuleBasedFlowSummary(currentWorkflow));

      if (!llmReady) return;

      const requestId = ++aiRequestRef.current;
      setAiLoading(true);
      try {
        const generated = await generateFlowSummaryAsync(currentWorkflow);
        if (aiRequestRef.current !== requestId) return;
        setSummary(generated);
      } catch (err) {
        if (aiRequestRef.current !== requestId) return;
        setError(err instanceof Error ? err.message : 'Could not summarize this flow');
        setSummary(buildRuleBasedFlowSummary(currentWorkflow));
      } finally {
        if (aiRequestRef.current === requestId) setAiLoading(false);
      }
    },
    [currentWorkflow, llmReady]
  );

  const handleAutoFix = useCallback(() => {
    if (!currentWorkflow || autoFixing) return;
    setAutoFixing(true);
    try {
      const safeOnly = currentWorkflow.missionMeta?.smartAutoFixPolicy === 'safe-only';
      const result = safeOnly ? autoFixWorkflowInlineSafe(currentWorkflow) : autoFixWorkflowInline(currentWorkflow);
      if (result.changes.length === 0) {
        toast.message('Smart Blocks found no changes needed');
        return;
      }
      const applied = applyAssembledWorkflowIfCurrent(currentWorkflow.id, result.workflow);
      if (!applied) {
        toast.warning('Mission changed — Smart Blocks was not applied');
        return;
      }
      saveWorkflow();
      invalidateFlowSummaryCache(currentWorkflow.id);
      addActivity(
        'Smart Blocks auto-fix',
        `${result.changes.length} change(s), policy: ${safeOnly ? 'safe-only' : 'safe-and-review'}`,
        'update'
      );
      toast.success(
        `Smart Blocks applied (${result.changes.length} changes · ${result.safetySummary.safeAutoFix} safe auto-fixes)`
      );
    } finally {
      setAutoFixing(false);
    }
  }, [currentWorkflow, autoFixing, applyAssembledWorkflowIfCurrent, saveWorkflow, addActivity]);

  const rollbackLastGood = useCallback(() => {
    if (!currentWorkflow || !lastGoodSnapshot) return;
    const applied = applyAssembledWorkflowIfCurrent(currentWorkflow.id, lastGoodSnapshot.workflow);
    if (!applied) {
      toast.warning('Could not roll back — mission changed');
      return;
    }
    saveWorkflow();
    invalidateFlowSummaryCache(currentWorkflow.id);
    addActivity('Flow rollback', 'Rolled back to last good tracing snapshot', 'update');
    toast.success('Rolled back to last good flow state');
  }, [currentWorkflow, lastGoodSnapshot, applyAssembledWorkflowIfCurrent, saveWorkflow, addActivity]);

  const needsMapWire =
    Boolean(currentWorkflow) &&
    isMinimalMissionFlow(currentWorkflow) &&
    Boolean(summary && summary.actions.length > 1);

  const wireSuggestedFlow = useCallback(async () => {
    if (!currentWorkflow) return;
    const intent = getMissionGoalIntent(currentWorkflow);
    if (intent.includes('Describe your outcome') || intent.includes('Add blocks')) {
      toast.error('Set the GOAL outcome on the map first.');
      return;
    }
    setWiring(true);
    setError(null);
    try {
      const { workflow } = await planAndApplyGoalToMap(intent);
      saveWorkflow();
      if (workflow?.id) invalidateFlowSummaryCache(workflow.id);
      toast.success(`Wired ${workflow?.nodes.length ?? 0} blocks to match your goal`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not wire blocks to map';
      setError(msg);
      toast.error(msg, { action: { label: 'Retry', onClick: () => void wireSuggestedFlow() } });
    } finally {
      setWiring(false);
    }
  }, [currentWorkflow, saveWorkflow]);

  if (!currentWorkflow) return null;

  const showAiBadge = summary?.source === 'ai' && llmReady;

  return (
    <div
      id="mission-flow-summary"
      className={cn('h-full min-h-0 border-l border-border bg-card overflow-hidden flex flex-col', className)}
    >
      <div className="p-4 border-b border-border shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary shrink-0" />
              <h3 className="font-semibold text-foreground truncate">Flow summary</h3>
              {aiLoading && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" aria-hidden />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{currentWorkflow.name}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {showAiBadge && (
              <Badge variant="secondary" className="text-[10px]">
                AI summary
              </Badge>
            )}
            {!llmReady && (
              <Badge variant="outline" className="text-[10px]">
                Live · Rules
              </Badge>
            )}
            {llmReady && !aiLoading && (
              <Badge variant="outline" className="text-[10px] hidden sm:inline-flex">
                Live sync
              </Badge>
            )}
            <Button
              variant={flowTracingMode ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 gap-1.5 px-2 text-[11px]"
              onClick={() => updateMissionMeta({ flowTracingMode: !flowTracingMode })}
              title="Toggle Flow Tracing highlighting"
            >
              <Radar className="h-3.5 w-3.5" />
              {flowTracingMode ? 'Tracing on' : 'Tracing off'}
            </Button>
            {flowTracingMode && (inlineValidation?.suggestions.length ?? 0) > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 px-2 text-[11px]"
                onClick={handleAutoFix}
                disabled={autoFixing}
                title="Smart Blocks — auto fix/configure this flow"
              >
                {autoFixing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <WandSparkles className="h-3.5 w-3.5" />
                )}
                Smart Blocks
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => void loadSummary(true)}
              disabled={aiLoading}
              aria-label="Refresh flow summary"
              title="Refresh summary"
            >
              <RefreshCw className={cn('h-4 w-4', aiLoading && 'animate-spin')} />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 space-y-5 overflow-auto min-h-0">
        {error && (
          <ContextualError
            message={error}
            action={{ label: 'Retry', onClick: () => void loadSummary(true) }}
          />
        )}

        {!summary ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Summarizing {PRODUCT_TERMS.mission.toLowerCase()}…
          </div>
        ) : (
          <>
            <section className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Target className="h-3.5 w-3.5" />
                Intent
              </div>
              <p className="text-sm text-foreground leading-relaxed">{summary.intent}</p>
            </section>

            <section className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" />
                What it does
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{summary.whatItDoes}</p>
            </section>

            <section className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <ListChecks className="h-3.5 w-3.5" />
                Actions to accomplish the goal
              </div>
              {needsMapWire && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Summary shows the solution for your GOAL — blocks are not on the map yet.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    className="w-full gap-2"
                    disabled={wiring || aiLoading}
                    onClick={() => void wireSuggestedFlow()}
                  >
                    {wiring ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Wand2 className="h-3.5 w-3.5" />
                    )}
                    Wire suggested blocks to map
                  </Button>
                </div>
              )}
              <ol className="space-y-2">
                {summary.actions.map((action) => (
                  <li
                    key={`${action.order}-${action.label}-${action.blockType}`}
                    className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5"
                  >
                    <div className="flex items-start gap-2">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-bold text-primary">
                        {action.order}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground leading-snug">{action.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{action.detail}</p>
                        <Badge variant="outline" className="mt-1.5 text-[9px] font-normal">
                          {action.blockType}
                        </Badge>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {inlineValidation && flowTracingMode && (
              <InlineValidationInsights
                analysis={inlineValidation}
                onFocusNode={(nodeId) => selectNode(nodeId)}
                liveStatus={liveStatus}
                truthFactor={truthFactor}
                compareSummary={
                  compare
                    ? `Compared to last good: ${Math.round(compare.confidenceDelta * 100)}% confidence, ${compare.nodeDelta} nodes, ${compare.edgeDelta} links`
                    : null
                }
                onRollbackLastGood={lastGoodSnapshot ? rollbackLastGood : undefined}
              />
            )}

            {aiLoading && llmReady && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />
                Syncing AI summary with map…
              </p>
            )}

            <p className="text-[11px] text-muted-foreground border-t border-border/50 pt-3">
              {needsMapWire
                ? 'Wire suggested blocks to update the canvas, or use the Map AI Assistant to replan.'
                : 'Updates as you edit the map. Turn Flow Tracing on to sync map highlights with inline validation suggestions.'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
